import React, { useEffect, useMemo } from 'react';
import { Button, Upload, UploadFile, message } from 'antd';
import { FileTextOutlined, PaperClipOutlined, PlayCircleOutlined, WarningOutlined } from '@ant-design/icons';

// Khung soạn nội dung + tệp đính kèm CSKH (tách từ TicketFooter.tsx cũ, 23/10/2026).
// Tệp chỉ gửi kèm nội dung qua AddResolveContentByStaff khi bấm nút gửi của nơi dùng.

type TPreviewImage = (images: { url: string; name: string }[], index: number) => void;

// Đính kèm (dùng chung cho tệp CSKH và tệp khách ở form Tạo phiếu): ảnh, video + Word / PDF / Excel. TỔNG các tệp gửi trong 1 lần tối đa 50MB
// (backend chặn theo tổng 1 lần gửi). Kiểm tra ngay lúc chọn để không phải chờ tới khi Lưu mới lỗi.
// Nút "Ảnh / video" mặc định chỉ lọc ảnh + video; tài liệu chọn qua nút riêng "Word / PDF / Excel"
// (trình duyệt không cho web đặt sẵn bộ lọc mặc định trong hộp thoại chọn tệp, nên tách 2 nút).
const MAX_FILE_MB = 50;
const MAX_BYTES = MAX_FILE_MB * 1024 * 1024;
const MEDIA_ACCEPT = 'image/*,video/*';
const DOC_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
const DOC_ACCEPT = DOC_EXTS.map(ext => `.${ext}`).join(',');
const isMediaFile = (type?: string) => !!type && (type.startsWith('image/') || type.startsWith('video/'));
const isDocFile = (name?: string) => DOC_EXTS.includes((name || '').split('.').pop()?.toLowerCase() || '');
const isAllowedFile = (file: File) => isMediaFile(file.type) || isDocFile(file.name);
const toMB = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

// Tổng dung lượng các tệp đang chờ gửi (byte)
export const totalBytes = (files: UploadFile[]) => {
  let total = 0;
  files.forEach(f => {
    total += Number(f.size || 0);
  });
  return total;
};

// Duyệt lần lượt các tệp của lượt chọn: tệp cộng vào vẫn ≤ 50MB thì nhận, tệp làm vượt thì bỏ.
// Vd đang chờ 30MB, chọn thêm [15MB, 10MB, 4MB] -> nhận 15MB (45), bỏ 10MB (55), nhận 4MB (49).
export const validateAttachment = (file: File, batch: File[], pendingBytes: number) => {
  if (!isAllowedFile(file)) {
    message.warning(`"${file.name}" không phải ảnh, video, Word, PDF hoặc Excel, không thể đính kèm.`);
    return Upload.LIST_IGNORE;
  }
  let total = pendingBytes;
  let found = false;
  let fits = false;
  batch.forEach(f => {
    if (found || !isAllowedFile(f)) return;
    const ok = total + f.size <= MAX_BYTES;
    if (ok) total += f.size;
    if (f === file) {
      found = true;
      fits = ok;
    }
  });
  // Không thấy tệp trong lượt chọn (phòng hờ): chỉ so với tệp đang chờ
  if (!found) fits = pendingBytes + file.size <= MAX_BYTES;
  if (!fits) {
    message.warning(
      `"${file.name}" (${toMB(file.size)}MB) làm tổng tệp đính kèm vượt ${MAX_FILE_MB}MB, không thể đính kèm.`
    );
    return Upload.LIST_IGNORE;
  }
  return false; // hợp lệ: giữ trong newFiles, chỉ tải lên khi bấm Lưu
};

// Khung soạn: ô nhập (children) + tệp chờ gửi + nút đính kèm. Tệp nằm trong newFiles, chỉ gửi khi bấm `sendLabel`.
// Ảnh/video chờ gửi hiện thumbnail (blob URL từ file đang chọn): ảnh bấm để xem lớn, video bấm mở tab mới.
// Dùng lại ở modal sửa nội dung xử lý (ResolveContentEditFields).
export const Composer = ({
  newFiles,
  setNewFiles,
  onPreviewImage,
  sendLabel,
  showNotice = true,
  children
}: {
  newFiles: UploadFile[];
  setNewFiles: (v: UploadFile[]) => void;
  onPreviewImage: TPreviewImage;
  sendLabel: string;
  // Dòng "Khách hàng đọc được…": bật cho nội dung CSKH gửi khách; tắt khi dùng cho tệp khách gửi (form Tạo phiếu)
  showNotice?: boolean;
  children?: React.ReactNode;
}) => {
  const items = useMemo(
    () =>
      newFiles.map(f => {
        const file = f.originFileObj as File | undefined;
        const isVideo = !!file && file.type.startsWith('video/');
        // PDF: trình duyệt xem được trong tab mới nên cũng tạo link tạm. Word / Excel không xem được, bấm chỉ tải lại
        // chính tệp vừa chọn nên không tạo link (ô chỉ hiện đuôi tệp).
        const isPdf = !!file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name));
        const hasLink = !!file && (isMediaFile(file.type) || isPdf);
        return { f, isVideo, isPdf, url: hasLink ? URL.createObjectURL(file as File) : '' };
      }),
    [newFiles]
  );
  // Thu hồi blob URL cũ khi danh sách đổi / đóng khung -> không rò bộ nhớ
  useEffect(() => () => items.forEach(i => i.url && URL.revokeObjectURL(i.url)), [items]);

  // Chỉ ảnh mới vào trình xem ảnh (video / PDF mở tab mới)
  const images = items.filter(i => i.url && !i.isVideo && !i.isPdf).map(i => ({ url: i.url, name: i.f.name }));
  const remove = (uid: string) => setNewFiles(newFiles.filter(x => x.uid !== uid));
  const pendingBytes = totalBytes(newFiles);

  // Dán ảnh chụp màn hình (Ctrl+V) thẳng vào khung -> thêm vào tệp chờ gửi, cùng luật kiểm tra như nút Đính kèm
  const onPaste = (e: React.ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (!pasted.length) return; // dán chữ -> để ô nhập xử lý bình thường
    e.preventDefault();
    const stamp = Date.now();
    const accepted: UploadFile[] = [];
    pasted.forEach((file, i) => {
      if (validateAttachment(file, pasted, pendingBytes) !== false) return;
      accepted.push({
        uid: `paste-${stamp}-${i}`,
        name: file.name || `anh-dan-${i + 1}.png`,
        size: file.size,
        type: file.type,
        originFileObj: file as any
      });
    });
    if (accepted.length) setNewFiles([...newFiles, ...accepted]);
  };

  return (
    <div
      className="rounded-md border border-gray-300 bg-white overflow-hidden focus-within:border-blue-400"
      onPaste={onPaste}
    >
      {children}
      {items.length > 0 && (
        <div
          className={`flex flex-wrap items-center gap-2 px-2 py-2 bg-gray-50 ${
            children ? 'border-t border-dashed border-gray-200' : ''
          }`}
        >
          <div className="w-full flex items-center gap-2">
            <span className="text-[12px] font-semibold text-yellow-700">Chờ gửi ({items.length})</span>
            <button
              type="button"
              className="ml-auto text-[12px] text-red-600 hover:underline"
              onClick={() => setNewFiles([])}
            >
              Bỏ hết
            </button>
          </div>
          {items.map(({ f, url, isVideo, isPdf }) => (
            <div key={f.uid} className="relative">
              {url && isPdf ? (
                // PDF: ô "PDF" như tài liệu khác nhưng bấm được, mở xem ở tab mới
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  title={`${f.name} (bấm để xem)`}
                  aria-label={`Xem PDF ${f.name}`}
                  className="flex flex-col items-center justify-center w-12 h-12 rounded border-2 border-dashed border-yellow-400 bg-yellow-50 px-0.5 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-900"
                >
                  <span className="text-[11px] font-bold uppercase leading-none">PDF</span>
                  <span className="w-full truncate text-center text-[9px] leading-tight">{f.name}</span>
                </a>
              ) : url && isVideo ? (
                // Video: khung hình đầu + biểu tượng phát; bấm mở video ở tab mới (trình xem ảnh không phát video)
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  title={`${f.name} (bấm để xem)`}
                  aria-label={`Xem video ${f.name}`}
                  className="relative block w-12 h-12 rounded border-2 border-dashed border-yellow-400 overflow-hidden bg-black"
                >
                  <video src={url} muted preload="metadata" className="w-full h-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center text-white text-[18px]">
                    <PlayCircleOutlined />
                  </span>
                </a>
              ) : url ? (
                <button
                  type="button"
                  title={`${f.name} (bấm để xem)`}
                  aria-label={`Xem ảnh ${f.name}`}
                  className="block w-12 h-12 rounded border-2 border-dashed border-yellow-400 overflow-hidden bg-white"
                  onClick={() =>
                    onPreviewImage(
                      images,
                      images.findIndex(i => i.url === url)
                    )
                  }
                >
                  <img src={url} alt={f.name} className="w-full h-full object-cover" />
                </button>
              ) : (
                <div
                  title={f.name}
                  className="flex flex-col items-center justify-center w-12 h-12 rounded border-2 border-dashed border-yellow-400 bg-yellow-50 px-0.5 text-yellow-800"
                >
                  <span className="text-[11px] font-bold uppercase leading-none">
                    {(f.name.split('.').pop() || 'tệp').slice(0, 4)}
                  </span>
                  <span className="w-full truncate text-center text-[9px] leading-tight">{f.name}</span>
                </div>
              )}
              <button
                type="button"
                aria-label={`Bỏ tệp ${f.name}`}
                title="Bỏ tệp"
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gray-700 text-white text-[10px] leading-none flex items-center justify-center hover:bg-red-600"
                onClick={() => remove(f.uid)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div
        className={`flex flex-wrap items-center gap-2 px-1.5 py-1 ${
          children || items.length ? 'border-t border-gray-100' : ''
        }`}
      >
        <Upload
          multiple
          accept={MEDIA_ACCEPT}
          fileList={newFiles}
          beforeUpload={(file, batch) => validateAttachment(file, batch, pendingBytes)}
          onChange={({ fileList }) => setNewFiles(fileList)}
          showUploadList={false}
        >
          <Button
            size="small"
            type="primary"
            ghost
            icon={<PaperClipOutlined style={{ transform: 'translateY(-3px)' }} />}
          >
            Ảnh / video
          </Button>
        </Upload>
        <Upload
          multiple
          accept={DOC_ACCEPT}
          fileList={newFiles}
          beforeUpload={(file, batch) => validateAttachment(file, batch, pendingBytes)}
          onChange={({ fileList }) => setNewFiles(fileList)}
          showUploadList={false}
        >
          <Button size="small" icon={<FileTextOutlined style={{ transform: 'translateY(-3px)' }} />}>
            Word / PDF / Excel
          </Button>
        </Upload>
        {/* Không cắt "…" nữa: hàng tự xuống dòng khi chật, chữ ngắn dễ hiểu */}
        <span
          className="ml-auto text-[12px] text-gray-600 tabular-nums"
          title={`Tệp chỉ được gửi khi bấm ${sendLabel}`}
        >
          {items.length ? `Đã chọn ${toMB(pendingBytes)} / ${MAX_FILE_MB}MB` : `Tối đa ${MAX_FILE_MB}MB mỗi lần gửi`}
        </span>
      </div>
      {/* Lưu ý: nội dung + tệp ở ô này gửi thẳng cho khách (chi tiết phiếu, tạo phiếu, sửa nội dung xử lý) */}
      {showNotice && (
        <div className="flex items-start gap-1.5 px-2.5 py-1.5 bg-amber-50 text-[12px] text-amber-800">
          <WarningOutlined className="text-amber-500 relative top-[3px]" />
          <span>
            <b>Lưu ý:</b> Khách hàng đọc được nội dung và tệp đính kèm ở mục này.
          </span>
        </div>
      )}
    </div>
  );
};
